import type { ReactElement } from "react";
import { BellOff, Trash2 } from "lucide-react";
import { space } from "@tokens/index";
import { Avatar } from "@primitives/Avatar";
import { Badge } from "@primitives/Badge";
import { IconButton } from "@primitives/IconButton";
import { Text } from "@primitives/Text";
import type { QueueMember } from "../../../experience-shell/queues";
import { formatAddedAt } from "./labels";
import styles from "./MemberRow.module.css";

export interface MemberRowProps {
    readonly member: QueueMember;
    readonly notificationsMuted: boolean;
    readonly canRemove: boolean;
    readonly isRemoving: boolean;
    /** The member joined during this session; the row settles from a tint. */
    readonly isFresh?: boolean;
    readonly onRemove: () => void;
}

export function MemberRow({
    member,
    notificationsMuted,
    canRemove,
    isRemoving,
    isFresh = false,
    onRemove,
}: MemberRowProps): ReactElement {
    return (
        <div className={styles.row} data-fresh={isFresh || undefined} data-testid="member-row">
            <Avatar alt={member.displayName} size="sm" />
            <div
                style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: space[2],
                    flex: 1,
                    minWidth: 0,
                }}
            >
                <Text role="bodySm" weight="medium" truncate>
                    {member.displayName}
                </Text>
                <Text role="caption" tone="muted" truncate style={{ flexShrink: 0 }}>
                    Added {formatAddedAt(member.addedAt)}
                </Text>
            </div>
            {notificationsMuted && (
                <Badge
                    variant="neutral"
                    size="sm"
                    caps={false}
                    title="This member has muted notifications for this queue"
                >
                    <BellOff size={12} strokeWidth={2} aria-hidden="true" />
                    <Text role="caption" style={{ color: "inherit", lineHeight: 1 }}>
                        Notifications off
                    </Text>
                </Badge>
            )}
            {canRemove && (
                <IconButton
                    className={styles.action}
                    icon={Trash2}
                    size="sm"
                    aria-label={`Remove ${member.displayName}`}
                    tooltip={`Remove ${member.displayName}`}
                    onClick={onRemove}
                    disabled={isRemoving}
                />
            )}
        </div>
    );
}
